import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dialog, Input, InlineError, Select } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useCreateTask } from '../tasks/hooks';
import { taskErrorMessage } from '../tasks/queries';
import { nextTaskPosition } from '../tasks/types';
import { useProjects, useTapPeople } from '../projects/hooks';
import { canEditProject } from '../projects/types';
import type { ProjectListItem, ProjectMemberWithProfile } from '../projects/types';
import type { CommentWithAuthor } from '../comments/types';
import { inertText } from './inertHtml';

/**
 * The "Make it a task" dialog (mockup/MakeTask.dc.html).
 *
 * Reuses useCreateTask (tasks/hooks.ts), the exact call every other task
 * creation flow in the app already makes — Projects, My Day (myDay/MyDayPage.tsx:10),
 * the task panel's own subtasks. No new task-creation code path.
 *
 * The task carries no room reference of its own: task rows have no
 * source-comment column and this drop adds no migration. "Kept to this
 * message" in the mockup is honoured in the room thread's own message body —
 * the created task is announced back into the room as a plain Hub link
 * (`/projects/<id>?task=<id>`), which the preview card (item 3) then draws.
 * That link is what "the room shows a chip that opens it" means here.
 *
 * "Goes in" (Dev4 review, Drop 3a follow-up B4): the old list was every
 * project the person could SEE (useProjects), which for an ordinary member
 * includes every all_tap project whether or not they can add tasks to it. It
 * also preselected the first one, which could be one they cannot use. The
 * list here is filtered with canEditProject (projects/types.ts:157), the
 * same check ProjectPage passes to its own Add task control
 * (ProjectPage.tsx:156, 673): a TAP Admin anywhere, the project owner, or a
 * project member holding the owner or member role. Nothing is preselected
 * until the person's own membership rows have loaded, so the box never shows
 * a project they cannot use as the "current" choice.
 */

export interface MakeTaskDialogProps {
  open: boolean;
  onClose: () => void;
  comment: CommentWithAuthor;
  sourceRoomName: string;
  /** Posts a follow-up message linking the new task back into the room. */
  onTaskCreated: (task: { id: string; projectId: string; title: string }) => void;
}

function plainTextOf(html: string): string {
  // Inert parse: a live div would load an <img onerror> from the body.
  return inertText(html);
}

/** A raw database refusal, never shown to the person as-is. */
function isPermissionRefusal(message: string): boolean {
  return /row-level security|permission denied/i.test(message);
}

export function MakeTaskDialog({
  open,
  onClose,
  comment,
  sourceRoomName,
  onTaskCreated,
}: MakeTaskDialogProps) {
  const { client, access } = useAuth();
  const profile = access.status === 'approved' ? access.profile : null;
  const projects = useProjects();
  const people = useTapPeople(open);

  // The person's own project_members rows, read only for the edit check
  // above: which of the projects they can see, they can also edit.
  const myMemberships = useQuery({
    queryKey: ['messages', 'make-task', 'my-memberships', profile?.id ?? ''],
    enabled: open && profile !== null,
    queryFn: async () => {
      const { data, error } = await client
        .from('project_members')
        .select('project_id, project_role')
        .eq('user_id', profile?.id ?? '');
      if (error) {
        throw new Error(error.message);
      }
      return data ?? [];
    },
  });

  const editableProjects: ProjectListItem[] = (() => {
    if (profile === null) {
      return [];
    }
    const membershipsByProject = new Map<string, ProjectMemberWithProfile['project_role'][]>();
    for (const row of myMemberships.data ?? []) {
      const list = membershipsByProject.get(row.project_id) ?? [];
      list.push(row.project_role);
      membershipsByProject.set(row.project_id, list);
    }
    return (projects.data ?? []).filter((project) => {
      const roles = membershipsByProject.get(project.id) ?? [];
      const members: ProjectMemberWithProfile[] = roles.map((role) => ({
        id: '',
        user_id: profile.id,
        project_role: role,
        display_name: '',
        email: '',
        avatar_url: null,
      }));
      return canEditProject(profile, project.owner_id, members);
    });
  })();

  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState(() => plainTextOf(comment.body).slice(0, 200));
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState('');

  const createTask = useCreateTask(projectId);

  // Only defaults to an editable project, and only once the membership read
  // has settled: never the first project in the unfiltered list.
  const effectiveProjectId = projectId !== '' ? projectId : (editableProjects[0]?.id ?? '');

  function submit() {
    if (title.trim() === '') {
      setError('Give the task a title.');
      return;
    }
    if (effectiveProjectId === '') {
      setError('Pick where the task goes.');
      return;
    }
    setError('');
    createTask.mutate(
      {
        projectId: effectiveProjectId,
        title: title.trim(),
        sectionId: null,
        statusId: null,
        assigneeId: assigneeId !== '' ? assigneeId : null,
        priority: null,
        dueDate: dueDate !== '' ? dueDate : null,
        position: nextTaskPosition([]),
      },
      {
        onSuccess: (task) => {
          onTaskCreated({
            id: task.id,
            projectId: task.project_id ?? effectiveProjectId,
            title: task.title,
          });
          onClose();
        },
        onError: (cause) => {
          const raw = cause instanceof Error ? cause.message : '';
          const mapped = raw === '' ? 'That task could not be created.' : taskErrorMessage(raw);
          // The database can still refuse this even after the list is
          // filtered (a membership can change between opening the dialog
          // and saving); a raw RLS refusal is never shown as-is.
          setError(isPermissionRefusal(mapped) ? "You can't add tasks to that project." : mapped);
        },
      },
    );
  }

  return (
    <Dialog open={open} title="Make it a task" onClose={onClose}>
      <div className="make-task-dialog">
        <div className="make-task-source">
          <span className="make-task-source-meta">
            {comment.author_name} in {sourceRoomName}
          </span>
          <p className="make-task-source-text">{plainTextOf(comment.body)}</p>
        </div>

        {error === '' ? null : <InlineError>{error}</InlineError>}

        <Input label="Task" value={title} onChange={(event) => setTitle(event.target.value)} />

        <div className="make-task-grid">
          <Select
            label="Goes in"
            value={effectiveProjectId}
            onChange={(event) => setProjectId(event.target.value)}
            options={editableProjects.map((project) => ({
              value: project.id,
              label: project.name,
            }))}
          />
          <Select
            label="Owner"
            value={assigneeId}
            onChange={(event) => setAssigneeId(event.target.value)}
            options={(people.data ?? []).map((person) => ({
              value: person.id,
              label: person.display_name.trim() !== '' ? person.display_name : person.email,
            }))}
            placeholder="Unassigned"
          />
          <Input
            label="Due"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        </div>

        <p className="make-task-note">
          The task gets its own thread, owner and done state. The room shows a chip that opens it.
        </p>

        <div className="make-task-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={createTask.isPending}>
            {createTask.isPending ? 'Creating…' : 'Create task'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
